import { z } from "zod";
import {
    optionalCounterpartyEmailSchema,
    optionalCounterpartyTextSchema,
} from "./counterparty.schema";

/**
 * A single person attached to a counterparty. Every content field is nullable;
 * the row must carry at least one non-null person field (enforced in the
 * service against the merged result, since PATCH is partial). `email` is
 * `citext` on the parent, so it is compared case-insensitively.
 */
export const counterpartyContactSchema = z.object({
    id: z.string(),
    counterpartyId: z.string(),
    label: z.string().nullable(),
    isPrimary: z.boolean(),
    title: z.string().nullable(),
    firstName: z.string().nullable(),
    middleName: z.string().nullable(),
    lastName: z.string().nullable(),
    suffix: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type CounterpartyContact = z.infer<typeof counterpartyContactSchema>;

/**
 * The user-authored content of a contact, shared between create and (partial)
 * update bodies. Empty strings normalize to `null` via the shared counterparty
 * text schemas.
 */
export const counterpartyContactContentFields = {
    label: optionalCounterpartyTextSchema,
    title: optionalCounterpartyTextSchema,
    firstName: optionalCounterpartyTextSchema,
    middleName: optionalCounterpartyTextSchema,
    lastName: optionalCounterpartyTextSchema,
    suffix: optionalCounterpartyTextSchema,
    email: optionalCounterpartyEmailSchema,
    phoneNumber: optionalCounterpartyTextSchema,
};

export const createCounterpartyContactBodySchema = z.object({
    ...counterpartyContactContentFields,
    isPrimary: z.boolean().optional(),
});

export type CreateCounterpartyContactBody = z.infer<
    typeof createCounterpartyContactBodySchema
>;

export const updateCounterpartyContactBodySchema = z
    .object(counterpartyContactContentFields)
    .refine((body) => Object.keys(body).length > 0, {
        message: "At least one field must be provided",
    });

export type UpdateCounterpartyContactBody = z.infer<
    typeof updateCounterpartyContactBodySchema
>;
